import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  fetchDashboardSummary,
  selectDashboardDataupBalance,
  selectDashboardDataupBalanceError,
  selectDashboardDataupBalanceLoading,
  selectDashboardSeagmBalance,
  selectDashboardSeagmBalanceError,
  selectDashboardSeagmBalanceLoading,
  selectDashboardTransactionStatusCounts,
  selectDashboardTotalUsers,
  selectDashboardTotalUsersError,
  selectDashboardTotalUsersLoading,
} from "../../../features/auth/store/dashboardStatSlice";
import { formatCurrency } from "../../../utils/utils";

const formatNumber = (value: number) => value.toLocaleString();

const CardSpinner = () => (
  <div className="flex items-center py-1">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
  </div>
);

type SummaryCardProps = {
  title: string;
  value: React.ReactNode;
  highlighted?: boolean;
  footer?: React.ReactNode;
};

const SummaryCard = ({
  title,
  value,
  highlighted = false,
  footer,
}: SummaryCardProps) => (
  <div
    className={
      highlighted
        ? "bg-pry-25 rounded-xl min-w-0"
        : "bg-warn-25 rounded-xl min-w-0"
    }
  >
    <div className="col-6 py-4 px-3">
      <div className="row-2 justify-between items-center">
        <span className="satoshi-sm-medium-gray-600 break-words">{title}</span>
      </div>
      <span className="satoshi-xl-bold-gray-900">{value}</span>
    </div>
    {footer}
  </div>
);

export const Summary = () => {
  const dispatch = useAppDispatch();
  const [refreshInterval, setRefreshInterval] = useState<number>(0);
  const dataupBalance = useAppSelector(selectDashboardDataupBalance);
  const dataupBalanceLoading = useAppSelector(
    selectDashboardDataupBalanceLoading,
  );
  const dataupBalanceError = useAppSelector(selectDashboardDataupBalanceError);
  const seagmBalance = useAppSelector(selectDashboardSeagmBalance);
  const seagmBalanceLoading = useAppSelector(
    selectDashboardSeagmBalanceLoading,
  );
  const seagmBalanceError = useAppSelector(selectDashboardSeagmBalanceError);
  const transactionStatusCounts = useAppSelector(
    selectDashboardTransactionStatusCounts,
  );
  const totalUsers = useAppSelector(selectDashboardTotalUsers);
  const totalUsersLoading = useAppSelector(selectDashboardTotalUsersLoading);
  const totalUsersError = useAppSelector(selectDashboardTotalUsersError);
  const isSummaryLoading =
    dataupBalanceLoading || seagmBalanceLoading || totalUsersLoading;
  useEffect(() => {
    dispatch(fetchDashboardSummary());
  }, [dispatch]);

  useEffect(() => {
    if (refreshInterval <= 0) return;

    const timer = window.setInterval(() => {
      dispatch(fetchDashboardSummary());
    }, refreshInterval);

    return () => window.clearInterval(timer);
  }, [dispatch, refreshInterval]);

  if (isSummaryLoading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center rounded-xl bg-white">
        <CardSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <select
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          className="border p-2 bg-[#F4F4F5] h-[40px] rounded-3xl appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          aria-label="Summary auto refresh interval"
        >
          <option value={0}>No refresh</option>
          <option value={30000}>30 seconds refresh</option>
          <option value={60000}>1 minute refresh</option>
          <option value={300000}>5 minutes refresh</option>
          <option value={600000}>10 minutes refresh</option>
          <option value={1800000}>30 minutes refresh</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <SummaryCard
          title="Seagm Credit Balance"
          highlighted={Boolean(seagmBalance && seagmBalance > 2000)}
          value={seagmBalanceError ? "Error" : formatNumber(seagmBalance ?? 0)}
        />

        <SummaryCard
          title="DataUp Balance"
          highlighted={Boolean(dataupBalance && dataupBalance > 200000)}
          value={
            dataupBalanceError
              ? "Error"
              : `₦${formatCurrency(dataupBalance ?? 0)}`
          }
        />

        <SummaryCard
          title="Total Users Onboarded"
          highlighted={Boolean(totalUsers && totalUsers > 100)}
          value={totalUsersError ? "Error" : formatNumber(totalUsers ?? 0)}
          footer={
            <Link
              to="/"
              className="flex border-t border-gray-200 py-3 px-6 w-full satoshi-sm-bold-gray-600 text-end"
            >
              View Users
            </Link>
          }
        />

        {transactionStatusCounts.map((item) => (
          <SummaryCard
            key={item.status}
            title={item.label}
            value={formatNumber(item.count)}
            highlighted={
              item.label === "SUCCESSFUL"
                ? item.count > 500
                : item.label === "REVERSED"
                  ? item.count < 50
                  : item.count < 10
            }
          />
        ))}
      </div>
    </div>
  );
};
